import { z } from 'zod';
import type { OfferLine, OfferSupplierGroup } from '@progear/finance-core';
import {
  cashFlowEntrySchema,
  type CashFlowEntry,
  type CashFlowRequestOptions,
} from '@/lib/cashFlow';

/**
 * Client for the ENRICHED ENTRY / DOCUMENT detail (replit.md module 1.3) — the one
 * drill surface shared by BOTH cash-flow lenses (L14). It talks to the single
 * `/api/cash-flow/entries/:id/detail` + `/override` surface, Zod-validates the
 * payload at the boundary (anti-slop rule), and exposes the typed shapes the
 * detail overlay renders. Money is integer cents (L9): this layer never computes
 * an amount — it only SELECTS the effective figures finance-core / the view own.
 *
 * The PATCH/override body is a per-entry classification correction (Liik / Projekt)
 * that WINS over the §1.11 mapping default: a field set to a value overrides it,
 * `null` clears it (revert to default), absent keeps it (mirrors the API contract).
 */

/** The 7-category enum, reused from the entry schema so the two never diverge. */
const categorySchema = cashFlowEntrySchema.shape.category;

/** One classification field: the effective value + what it resolves FROM (the
 *  §1.11 mapping default and the per-entry override; override WINS when set). */
function classificationField<T extends z.ZodTypeAny>(value: T) {
  return z.object({
    effective: value,
    default: value,
    override: value.nullable(),
  });
}

const classificationSchema = z.object({
  category: classificationField(categorySchema),
  project: classificationField(z.string().nullable()),
  projectName: classificationField(z.string().nullable()),
});

/** A source-document link as returned to the web.
 *  - `url` is the PDF inline render source: a short-lived signed URL into our PRIVATE
 *    bucket (the self-hosted mirror), null until mirrored — NEVER a vendor URL (the
 *    vendor CSP forbids inline framing, so only our mirror renders in the iframe).
 *  - `externalUrl` is the vendor original (Erply invoiceLink). For an Erply sales
 *    invoice it is BOTH the "Ava originaalis" new-tab fallback AND the source the
 *    printout is LIVE-FETCHED from on open (the raw HTML is never stored/inlined on
 *    this payload — see {@link fetchCashFlowEntryDocumentHtml}); null for self-only
 *    sources (SA PDF).
 *
 *  The Erply printout HTML is deliberately NOT part of this payload: it is fetched RAW
 *  from the vendor on document open (v1 parity, always fresh) rather than mirrored. */
const documentSchema = z.object({
  kind: z.enum(['smartaccounts_pdf', 'erply_html']),
  cacheStatus: z.enum(['pending', 'cached', 'error']),
  mimeType: z.string().nullable(),
  externalDocumentId: z.string().nullable(),
  url: z.string().nullable(),
  externalUrl: z.string().nullable(),
  // Deep-link into the Erply back-office editor (the "Ava Erplys" action); built
  // server-side from the account client code, null for non-Erply / unconfigured docs.
  backofficeUrl: z.string().nullable(),
});

/**
 * The linked Erply OFFER (pakkumine) surfaced on the enriched detail (module 1.3,
 * the rich offer view). The supplier groups + line/group nets are computed ONCE by
 * finance-core (master rule; L9) and threaded through the API — the web NEVER
 * re-aggregates them. These schemas are typed against the finance-core `OfferLine`
 * / `OfferSupplierGroup` shapes so the wire contract can never silently drift from
 * the one definition. `amounts`/`document` are null until Erply's figures / the
 * sanitized printout are synced; the whole `offer` is null for an ORDER-based
 * invoice that stems from no offer.
 */
const offerLineSchema: z.ZodType<OfferLine> = z.object({
  rowIndex: z.number(),
  productCode: z.string().nullable(),
  itemName: z.string().nullable(),
  qty: z.number(),
  lineNetCents: z.number(),
  supplierExternalId: z.string().nullable(),
  supplierName: z.string().nullable(),
});

const offerSupplierGroupSchema: z.ZodType<OfferSupplierGroup> = z.object({
  supplierExternalId: z.string().nullable(),
  supplierName: z.string().nullable(),
  lines: z.array(offerLineSchema),
  netCents: z.number(),
  lineCount: z.number(),
});

/** The offer's document-level amounts, integer cents (L9) — null until synced. */
const offerAmountsSchema = z.object({
  netCents: z.number(),
  vatCents: z.number(),
  grossCents: z.number(),
});

/** The offer's sanitized printout (always Erply HTML). `html` is the inlined bytes
 *  the viewer frames via `srcDoc` (into which it injects its highlight engine);
 *  null until mirrored. `externalUrl` is the vendor original for the "Ava
 *  originaalis" new-tab fallback ONLY — never the iframe src. */
const offerDocumentSchema = z.object({
  kind: z.literal('erply_html'),
  cacheStatus: z.enum(['pending', 'cached', 'error']),
  externalUrl: z.string().nullable(),
  html: z.string().nullable(),
});

const offerSchema = z.object({
  docNumber: z.string().nullable(),
  documentDate: z.string().nullable(),
  party: z.string(),
  amounts: offerAmountsSchema.nullable(),
  groups: z.array(offerSupplierGroupSchema),
  document: offerDocumentSchema.nullable(),
});

/** One OTHER component entry of the same loan installment (cross-reference target). */
const loanInstallmentSiblingSchema = z.object({
  entryId: z.string(),
  part: z.enum(['principal', 'interest', 'fee']),
  description: z.string().nullable(),
  netCents: z.number(),
  grossCents: z.number(),
});

/** The "Laenu osamakse" cross-reference on a materialized loan entry: a payment
 *  splits into principal + interest (+ fee) rows, so the detail carries the whole
 *  payment's total (summed by the API via finance-core, L9 — never re-added here)
 *  and links to the sibling entries. Null on everything else. */
const loanInstallmentSchema = z.object({
  seq: z.number(),
  part: z.enum(['principal', 'interest', 'fee']),
  totalNetCents: z.number(),
  totalGrossCents: z.number(),
  siblings: z.array(loanInstallmentSiblingSchema),
});

export const cashFlowEntryDetailSchema = z.object({
  entry: cashFlowEntrySchema,
  classification: classificationSchema,
  document: documentSchema.nullable(),
  offer: offerSchema.nullable(),
  loanInstallment: loanInstallmentSchema.nullable(),
});

const detailEnvelopeSchema = z.object({ detail: cashFlowEntryDetailSchema });

export type EntryClassification = z.infer<typeof classificationSchema>;
export type CashFlowEntryDocument = z.infer<typeof documentSchema>;
export type CashFlowOfferAmounts = z.infer<typeof offerAmountsSchema>;
export type CashFlowOfferDocument = z.infer<typeof offerDocumentSchema>;
export type CashFlowOffer = z.infer<typeof offerSchema>;
export type LoanInstallmentSibling = z.infer<typeof loanInstallmentSiblingSchema>;
export type LoanInstallmentRef = z.infer<typeof loanInstallmentSchema>;
export type CashFlowEntryDetail = z.infer<typeof cashFlowEntryDetailSchema>;

/** Override-patch body: each field overrides when a value, reverts when `null`,
 *  keeps when absent. project/projectName are free text (the web sends `null`,
 *  never `""`, to clear — mirrors the API's `min(1)` guard). */
export interface OverridePatch {
  category?: CashFlowEntry['category'] | null;
  project?: string | null;
  projectName?: string | null;
}

function authHeaders(
  accessToken: string | undefined,
  base: Record<string, string> = {},
): Record<string, string> {
  return accessToken ? { ...base, Authorization: `Bearer ${accessToken}` } : base;
}

/** Read the enriched-entry detail (effective entry + classification provenance +
 *  linked source document) for one entry. */
export async function fetchCashFlowEntryDetail(
  id: string,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntryDetail> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/${id}/detail`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow detail request failed: ${res.status}`);
  }
  return detailEnvelopeSchema.parse(await res.json()).detail;
}

/** The live document-HTML envelope: the RAW vendor printout bytes, fetched fresh on
 *  open, plus the WORKING vendor link the API resolved for that fetch. `html` is
 *  unsanitized text/html to be framed VERBATIM via a `sandbox=""` `srcDoc` (v1
 *  parity) — the pane never stores or normalizes it. `externalUrl` is what "Ava
 *  originaalis" must open: Erply ROTATES the stored link's checksum, so the
 *  freshly-resolved link is the only one guaranteed not to 404. */
const documentHtmlEnvelopeSchema = z.object({ html: z.string(), externalUrl: z.string() });
export type CashFlowDocumentHtmlPayload = z.infer<typeof documentHtmlEnvelopeSchema>;

/**
 * LIVE-fetch the RAW Erply printout HTML for one entry (module 1.3, right pane). The
 * API reads the entry's `external_url` server-side and proxies the vendor GET, so the
 * web only needs the entry id. The bytes come back UNMODIFIED (no sanitize, no mirror)
 * to be framed verbatim in a sandboxed `srcDoc`; this runs on EVERY open (never a
 * stored mirror) so the pane always shows the current vendor document.
 */
export async function fetchCashFlowEntryDocumentHtml(
  id: string,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowDocumentHtmlPayload> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/${id}/document-html`, {
    headers: authHeaders(accessToken),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow document HTML request failed: ${res.status}`);
  }
  return documentHtmlEnvelopeSchema.parse(await res.json());
}

/** Persist a classification correction (Liik / Projekt) and return the re-read
 *  detail (the API re-projects the entry's effective classification + search row). */
export async function updateCashFlowEntryOverride(
  id: string,
  patch: OverridePatch,
  options: CashFlowRequestOptions = {},
): Promise<CashFlowEntryDetail> {
  const { fetchImpl = fetch, baseUrl = '', accessToken } = options;
  const res = await fetchImpl(`${baseUrl}/api/cash-flow/entries/${id}/override`, {
    method: 'PATCH',
    headers: authHeaders(accessToken, { 'Content-Type': 'application/json' }),
    body: JSON.stringify(patch),
  });
  if (!res.ok) {
    throw new Error(`Cash-flow override failed: ${res.status}`);
  }
  return detailEnvelopeSchema.parse(await res.json()).detail;
}

// ----------------------------------------------------------------- pure helpers

/**
 * How the RIGHT document pane should render, resolved from the linked document:
 * - `none`      — no document linked, OR an Erply pointer with no vendor link (nothing
 *                 to live-fetch and nothing to open) → the empty state.
 * - `pending`   — a SmartAccounts PDF linked but not yet mirrored (no signed `url`).
 *                 Carries `externalUrl` (null for a PDF, which has no vendor original).
 * - `pdf`       — a SmartAccounts PDF from our signed URL, in a plain iframe (no scripts).
 * - `html-live` — an Erply HTML invoice whose raw bytes are LIVE-FETCHED on open (v1
 *                 parity; see {@link fetchCashFlowEntryDocumentHtml}) and framed VERBATIM
 *                 via a `sandbox=""` `srcDoc`. The pane owns that fetch's own loading /
 *                 error indicator; `externalUrl` is BOTH the fetch source (server-side)
 *                 and the "Ava originaalis" fallback, so it is non-null in this mode.
 */
export type DocumentPaneState =
  | { mode: 'none' }
  | { mode: 'pending'; label: string; badge: 'PDF'; sandboxed: false; externalUrl: string | null }
  | {
      mode: 'html-live';
      externalUrl: string;
      backofficeUrl: string | null;
      label: string;
      badge: 'HTML';
      sandboxed: true;
    }
  | { mode: 'pdf'; url: string; label: string; badge: 'PDF'; sandboxed: false };

export function documentPaneState(document: CashFlowEntryDocument | null): DocumentPaneState {
  if (!document) {
    return { mode: 'none' };
  }
  if (document.kind === 'erply_html') {
    const label = 'Erply müügiarve';
    // The Erply printout is NOT stored: it is fetched RAW from the vendor on open and
    // framed verbatim (v1 parity). `externalUrl` is BOTH that server-side fetch source
    // and the "Ava originaalis" fallback, so it gates the pane — null → nothing to fetch
    // and nothing to open → the empty state (a sales invoice always carries its Erply
    // invoiceLink in practice, so this is the anomalous, not the normal, arm).
    if (document.externalUrl === null) {
      return { mode: 'none' };
    }
    return {
      mode: 'html-live',
      externalUrl: document.externalUrl,
      backofficeUrl: document.backofficeUrl,
      label,
      badge: 'HTML',
      sandboxed: true,
    };
  }
  const label = 'SmartAccounts ostuarve';
  // The PDF renders from our signed mirror `url` (application/pdf serves correctly).
  // Until it exists the pane is 'pending'; a PDF has no vendor fallback (externalUrl).
  if (document.url === null) {
    return { mode: 'pending', label, badge: 'PDF', sandboxed: false, externalUrl: document.externalUrl };
  }
  return { mode: 'pdf', url: document.url, label, badge: 'PDF', sandboxed: false };
}

/** The editable classification draft, seeded from the EFFECTIVE values (what the
 *  user currently sees). category is always set; project/projectName may be null. */
export interface OverrideDraft {
  category: CashFlowEntry['category'];
  project: string | null;
  projectName: string | null;
}

export function overrideDraftFrom(classification: EntryClassification): OverrideDraft {
  return {
    category: classification.category.effective,
    project: classification.project.effective,
    projectName: classification.projectName.effective,
  };
}

/** The mapping defaults (§1.11) the "Taasta vaikimisi" action reverts to. */
export function overrideDefaultsFrom(classification: EntryClassification): OverrideDraft {
  return {
    category: classification.category.default,
    project: classification.project.default,
    projectName: classification.projectName.default,
  };
}

/** Whether the draft differs from the current effective classification (Save gate). */
export function isOverrideDirty(draft: OverrideDraft, classification: EntryClassification): boolean {
  return (
    draft.category !== classification.category.effective ||
    draft.project !== classification.project.effective ||
    draft.projectName !== classification.projectName.effective
  );
}

/** Whether ANY field currently carries a per-entry override (shows "Taasta vaikimisi"). */
export function hasActiveOverride(classification: EntryClassification): boolean {
  return (
    classification.category.override !== null ||
    classification.project.override !== null ||
    classification.projectName.override !== null
  );
}

/**
 * Map the draft to the override PATCH body: a field equal to its mapping default
 * sends `null` (clear the override, revert to default); a field differing from the
 * default sends the value (set the override). Sending the full desired state each
 * save keeps the override row = exactly the fields that differ from the default
 * (all-null → the API deletes the row).
 */
export function overridePatchFrom(
  draft: OverrideDraft,
  classification: EntryClassification,
): OverridePatch {
  return {
    category: draft.category === classification.category.default ? null : draft.category,
    project: draft.project === classification.project.default ? null : draft.project,
    projectName:
      draft.projectName === classification.projectName.default ? null : draft.projectName,
  };
}
